import { ApplicationConfig, importProvidersFrom, provideBrowserGlobalErrorListeners, provideZonelessChangeDetection } from '@angular/core';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { provideRouter } from '@angular/router';
import { AppConfig } from './shared/services/config.service';
import { routes } from './app.routes';
import { API_BASE_URL as SAMV2_API_BASE_URL, AuthConfig, AuthModule, EnvironmentConfig, LANG_CONFIG, SAMV2_AUTH_CONFIG, SAMV2_LOGGER_CONFIG, SAMV2_CONTEXT_CONFIG, SAMV2_ENV_CONFIG, LoggerConfig, Samv2DesktopModule, Samv2ProvidersModule } from '@aubert-et-duval/samv2';
import { MSAL_INTERCEPTOR_CONFIG, MsalInterceptorConfiguration } from '@azure/msal-angular';
import { InteractionType } from '@azure/msal-browser';
import { API_BASE_URL } from '@services/service-clients';
import { IconsModule } from './shared/modules/dates/icons/icons.module';
import { DatesModule } from './shared/modules/dates/dates.module';
import {
  AnomalieDemandeTransportClient,
  ArticleClient,
  CarteClient,
  ColisClient,
  CommentaireClient,
  DemandeApproTagClient,
  DemandeExpeditionClient,
  DemandeTransportClient,
  IntegrationClient,
  InterruptionClient,
  LocalisationClient,
  NotificationClient,
  OutgoingClient,
  PieceClient,
  PriorisationParametrageClient,
  RoleClient,
  SAMClient,
  SAMHealthCheckClient,
  ScheduledTaskClient,
  SettingClient,
  SignalrClient,
  TagClient,
  UserClient,
  ZoneClient,
} from './shared/services/service-clients';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { TouaregControlDisplayService } from './modules/touareg-control/services/touareg-control-display.service';
import { TouaregControlStateHealthMonitorService } from './modules/touareg-control/services/touareg-control-state-health-monitor.service';
import { TouaregControlStateMachineService } from './modules/touareg-control/services/touareg-control-state-machine.service';

export function MSALInterceptorConfigFactory(authConfig: AuthConfig, envConfig: EnvironmentConfig): MsalInterceptorConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap: new Map([
      ['https://graph.microsoft.com/v1.0/me', ['user.read']],
      ['api://' + authConfig.clientId, ['user.read']],
      [envConfig.backendUrl + '/api/*', [authConfig.clientId + '/.default']]
    ])
  };
}

export function createAppConfig(config: AppConfig): ApplicationConfig {
  return {
    providers: [
      provideZonelessChangeDetection(),
      provideBrowserGlobalErrorListeners(),
      { provide: API_BASE_URL, useValue: config.backendUrl },
      { provide: SAMV2_API_BASE_URL, useExisting: API_BASE_URL },
      { provide: SAMV2_CONTEXT_CONFIG, useValue: { context: [] } },
      {
        provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
        useValue: { appearance: 'outline' }
      },
      {
        provide: SAMV2_ENV_CONFIG,
        useValue: {
          production: config.production,
          published: config.published,
          backendUrl: config.backendUrl,
          baseUrl: config.baseUrl,
          appName: config.appName,
          envName: config.envName,
          version: config.version,
          appInsightsKey: '',
        } as EnvironmentConfig,
      },
      {
        provide: SAMV2_AUTH_CONFIG,
        useValue: {
          clientId: config.auth.clientId,
          tenantId: config.auth.tenantId,
        } as AuthConfig,
      },
      {
        provide: SAMV2_LOGGER_CONFIG,
        useValue: {
          logLevel: config.logger.logLevel,
          logPii: config.logger.logPii,
        } as LoggerConfig,
      },
      {
        provide: LANG_CONFIG,
        useValue: {
          availableLangs: ['fr', 'en'],
          defaultLang: 'fr'
        }
      },
      importProvidersFrom(Samv2ProvidersModule),
      importProvidersFrom(Samv2DesktopModule),
      importProvidersFrom(AuthModule),
      importProvidersFrom(IconsModule),
      importProvidersFrom(DatesModule),
      AnomalieDemandeTransportClient,
      ArticleClient,
      CarteClient,
      ColisClient,
      CommentaireClient,
      DemandeApproTagClient,
      DemandeExpeditionClient,
      DemandeTransportClient,
      IntegrationClient,
      InterruptionClient,
      LocalisationClient,
      NotificationClient,
      OutgoingClient,
      PieceClient,
      PriorisationParametrageClient,
      RoleClient,
      SAMClient,
      SAMHealthCheckClient,
      ScheduledTaskClient,
      SettingClient,
      SignalrClient,
      TagClient,
      UserClient,
      ZoneClient,
      TouaregControlDisplayService,
      TouaregControlStateMachineService,
      TouaregControlStateHealthMonitorService,
      {
        provide: MSAL_INTERCEPTOR_CONFIG,
        useFactory: MSALInterceptorConfigFactory,
        deps: [SAMV2_AUTH_CONFIG, SAMV2_ENV_CONFIG]
      },
      provideRouter(routes),
    ],
  };
}
